import { useState, useEffect } from 'react';
import type { FC, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MarketingLayout from '../components/MarketingLayout';
import { useTranslation } from '../i18n/I18nContext';
import { Spinner } from '../components/ui/spinner';
import { captureReferralCodeFromUrl } from '../utils/referral-code';

const AuthEmail: FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const { isSessionValid, refreshSession } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Capture ?ref=<code> into localStorage before the user triggers the
  // email signup flow (which navigates to the verification page and
  // would otherwise lose the query string). See utils/referral-code.ts.
  useEffect(() => {
    captureReferralCodeFromUrl();
  }, []);

  useEffect(() => {
    if (isSessionValid) {
      navigate('/articles');
    }
  }, [isSessionValid, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) return;
    if (password.length < 8) {
      setError(t('auth.password_too_short'));
      return;
    }

    setIsLoading(true);

    try {
      const { signUp } = await import('aws-amplify/auth');
      await signUp({
        username: trimmedEmail,
        password,
        options: { userAttributes: { email: trimmedEmail } },
      });

      // New user — needs verification
      navigate('/auth/verify', { state: { email: trimmedEmail, password } });
    } catch (signUpError: any) {
      const errorName = signUpError?.name || '';

      if (errorName === 'UsernameExistsException') {
        // User exists — try sign in
        try {
          const { signIn } = await import('aws-amplify/auth');
          await signIn({ username: trimmedEmail, password });
          await refreshSession();
          navigate('/articles', { replace: true });
        } catch (signInError: any) {
          const signInErrorName = signInError?.name || '';

          if (signInErrorName === 'UserNotConfirmedException') {
            // Signed up but never confirmed — resend code
            const { resendSignUpCode } = await import('aws-amplify/auth');
            await resendSignUpCode({ username: trimmedEmail });
            navigate('/auth/verify', { state: { email: trimmedEmail, password } });
          } else if (signInErrorName === 'NotAuthorizedException') {
            setError(t('auth.incorrect_password'));
            setIsLoading(false);
          } else {
            setError(signInError instanceof Error ? signInError.message : t('auth.error_generic'));
            setIsLoading(false);
          }
        }
      } else if (signUpError?.message?.includes('already exists') && signUpError?.message?.includes('Google')) {
        // PreSignUp Lambda blocked signup — email belongs to a Google account
        setError(t('auth.use_google_instead'));
        setIsLoading(false);
      } else {
        // Surface Cognito's message directly (e.g. InvalidPasswordException)
        setError(signUpError instanceof Error ? signUpError.message : t('auth.error_generic'));
        setIsLoading(false);
      }
    }
  };

  const handleForgotPassword = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      setError(t('auth.enter_email_first'));
      return;
    }

    setResetSending(true);
    setError('');

    try {
      const { resetPassword } = await import('aws-amplify/auth');
      await resetPassword({ username: trimmedEmail });
      navigate('/auth/reset', { state: { email: trimmedEmail } });
    } catch (err: any) {
      const errorName = err?.name || '';
      if (errorName === 'UserNotFoundException') {
        setError(t('auth.no_account_found'));
      } else if (errorName === 'LimitExceededException') {
        setError(t('auth.too_many_attempts'));
      } else {
        setError(err instanceof Error ? err.message : t('auth.error_generic'));
      }
      setResetSending(false);
    }
  };

  return (
    <MarketingLayout>
      <div className="max-w-sm mx-auto px-4 pt-8 pb-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {t('auth.email_heading')}
          </h1>
          <p className="text-muted-foreground">
            {t('auth.email_description')}
          </p>
        </div>

        {error && (
          <div className="bg-destructive text-destructive-foreground rounded-md p-3 mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('auth.email_label')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading || resetSending}
              required
              autoFocus
              autoComplete="email"
              className="w-full border border-input rounded-md px-3 py-2.5 bg-background text-foreground text-sm"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium">{t('auth.password_label')}</label>
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={isLoading || resetSending}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {resetSending ? t('auth.sending_reset') : t('auth.forgot_password')}
              </button>
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading || resetSending}
              required
              minLength={8}
              autoComplete="current-password"
              className="w-full border border-input rounded-md px-3 py-2.5 bg-background text-foreground text-sm"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || resetSending || !email.trim() || password.length < 8}
            className="w-full bg-success text-success-foreground px-6 py-3 rounded-lg text-base font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Spinner size="sm" className="border-success-foreground" />
                {t('auth.signing_in')}
              </span>
            ) : (
              t('auth.continue')
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/auth')}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('auth.back_to_login')}
          </button>
        </div>
      </div>
    </MarketingLayout>
  );
};

export default AuthEmail;
